import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './cn';
import { Field, fieldControlClassName, fieldDescribedBy } from './Field';

/**
 * Token-first single-value dropdown (ADR-015 amendment 2026-07-07). Deliberately
 * wraps the NATIVE `<select>` (retains `HTMLSelectElement` semantics) — native is the
 * most accessible and touch-friendly choice for plain single-value pickers; custom
 * listboxes are reserved for master–detail directories (`DirectoryList`) and multi-
 * select recipient lists (`MultiSelectDirectoryList`). Adds the shared control
 * surface, a decorative chevron, and optional `Field` labelling.
 */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  containerClassName?: string;
  children: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    id,
    label,
    description,
    error,
    required,
    className,
    containerClassName,
    children,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const control = (
    <div className="relative">
      <select
        ref={ref}
        id={controlId}
        required={required}
        aria-invalid={error ? true : props['aria-invalid']}
        aria-describedby={fieldDescribedBy(controlId, {
          description: label ? description : undefined,
          error: label ? error : undefined,
          extra: ariaDescribedBy,
        })}
        className={cn(
          fieldControlClassName,
          'cursor-pointer appearance-none pr-9',
          error && 'border-danger',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden
        focusable={false}
        className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-secondary"
      />
    </div>
  );

  if (!label) {
    return control;
  }

  return (
    <Field
      htmlFor={controlId}
      label={label}
      description={description}
      error={error}
      required={required}
      className={containerClassName}
    >
      {control}
    </Field>
  );
});
